// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — `features.query`: the AYNI chatbot reads the records a DSA 1.8
 * vector layer draws (the quakes' magnitude, depth and place), by their
 * fields, never from colours (AYNI 3D's
 * `docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md` §4.4).
 *
 * The features are the vector player's (`VectorPlayer`): "shown" is the
 * snapshot of the frame on screen, the same set a paused globe draws (§6:
 * the frame's window, a day for the quakes); "all" is every record of the
 * layer, narrowed by `from`/`to`. Then the box and the field conditions,
 * then the ranking. Pure: no DOM, no player; `main.ts` hands it the layers.
 *
 * Also `featuresSituation`: one line for the turn's context, so the model
 * knows the globe draws records it can read.
 */
import type { AgentCaution, AgentCommand, AgentLatLonBounds, AgentToolOutcome } from '../ayni3d/shared/protocol'
import type { CompiledVectorLayer, LonLat, VectorFeature } from '../services/vector/vectorModel'
import type { VectorField } from '../services/vector/vectorTypes'
import type { VectorClock } from '../services/vector/vectorTimeline'
import { frameEndAt, timelineEnd } from '../services/vector/vectorTimeline'
import { inspectFeature } from '../services/vector/vectorPick'
import { localize } from '../services/vector/vectorStyle'
import { ok, unavailable } from './xrChatOutcomes'

/** What the executor needs of the primary globe's vector layers. */
export interface XrChatFeatureSource {
  /** The layers drawn, in `order`. */
  layers: readonly CompiledVectorLayer[]
  /** The data clock (§6), or null for static layers. */
  clock: VectorClock | null
  /** The data time the layers are at now (Unix s), or null. */
  nowS: number | null
  /** The features a layer shows for the frame on screen (its snapshot). */
  shown(layer: CompiledVectorLayer): readonly VectorFeature[]
  /** The globe is playing: "shown" moves on. */
  playing: boolean
}

type Query = Extract<AgentCommand, { type: 'features.query' }>

const DEFAULT_LIMIT = 5
const MAX_LIMIT = 10
const DAY_S = 86_400
/** Outcomes are English; the model answers in the visitor's language. */
const LANG = 'en'

export const FEATURES_METHOD =
  "Read from the dataset's own records (its DSA 1.8 vector layer), not from colours on the globe: the records of the period, then the box and the conditions, then ranked by the field asked for."

const NUMERIC = new Set(['number', 'integer'])

const isoS = (s: number | undefined): string | null =>
  typeof s === 'number' && Number.isFinite(s) ? new Date(s * 1000).toISOString().replace('.000Z', 'Z') : null

/** A field's name for people: its English label, else its name. */
const labelOf = (f: VectorField): string => localize(f.label, LANG) || f.name

/** The fields a reader cares about: attributes (role `attribute`). */
const attributes = (layer: CompiledVectorLayer): VectorField[] => layer.spec.fields.filter((f) => f.role === 'attribute' && f.type !== 'coordinates' && f.type !== 'times')

/** A field by its name or its label, ignoring case. */
function fieldNamed(fields: readonly VectorField[], name: string): VectorField | undefined {
  const n = name.trim().toLowerCase()
  return fields.find((f) => f.name.toLowerCase() === n) ?? fields.find((f) => labelOf(f).toLowerCase() === n)
}

/** A number to the field's precision (one more digit for means), else 3 decimals. */
function round(value: number, field: VectorField | undefined, extra = 0): number {
  const digits = Math.min(6, (field?.precision ?? 3) + extra)
  return Number(value.toFixed(digits))
}

/** When a feature happens: [first, last] Unix s; null for static features. */
function span(layer: CompiledVectorLayer, f: VectorFeature): [number, number] | null {
  switch (layer.spec.time.mode) {
    case 'instant':
      return f.time === undefined ? null : [f.time, f.time]
    case 'interval':
      return f.start === undefined ? null : [f.start, f.end ?? Infinity]
    case 'vertex':
      return f.vertexTimes?.length ? [f.vertexTimes[0], f.vertexTimes[f.vertexTimes.length - 1]] : null
    default:
      return null
  }
}

function vertices(f: VectorFeature): LonLat[] {
  if (f.point) return [f.point]
  if (f.parts) return f.parts.flat()
  if (f.polygons) return f.polygons.flatMap((rings) => rings[0] ?? [])
  return []
}

/** Where to say a feature is: the point, a line's or track's first vertex, an area's mean vertex. */
function placeOf(f: VectorFeature): LonLat | null {
  if (f.point) return f.point
  if (f.parts?.[0]?.length) return f.parts[0][0]
  const ring = f.polygons?.[0]?.[0]
  if (!ring?.length) return null
  const n = ring.length - 1 || 1
  return [ring.slice(0, n).reduce((s, p) => s + p[0], 0) / n, ring.slice(0, n).reduce((s, p) => s + p[1], 0) / n]
}

function inBounds([lon, lat]: LonLat, b: AgentLatLonBounds): boolean {
  if (lat < b.s || lat > b.n) return false
  return b.w <= b.e ? lon >= b.w && lon <= b.e : lon >= b.w || lon <= b.e
}

/** A date argument as Unix s; a bare date `to` counts its whole day. */
function dateS(value: string, endOfDay: boolean): number {
  const s = Date.parse(value) / 1000
  return endOfDay && /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) ? s + DAY_S : s
}

function fieldList(fields: readonly VectorField[]): string {
  return fields.map((f) => `${f.name} (${labelOf(f)}${f.units ? `, ${f.units}` : ''})`).join(', ')
}

/** The layer the query names, else the topmost. */
function pickLayer(layers: readonly CompiledVectorLayer[], name: string | undefined): CompiledVectorLayer | null {
  if (!name) return layers[layers.length - 1] ?? null
  const n = name.trim().toLowerCase()
  return layers.find((l) => l.name.toLowerCase() === n) ?? layers.find((l) => l.name.toLowerCase().includes(n)) ?? null
}

/** Does a cell meet one condition? Text matches within, ignoring case. */
function meets(value: unknown, c: { min?: number; max?: number; equals?: string | number | boolean }): boolean {
  if (c.min !== undefined || c.max !== undefined) {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false
    if (c.min !== undefined && value < c.min) return false
    if (c.max !== undefined && value > c.max) return false
  }
  if (c.equals !== undefined) {
    if (typeof c.equals === 'string' && typeof value === 'string') return value.toLowerCase().includes(c.equals.toLowerCase())
    // 5 matches "5" in a category column.
    return value !== null && value !== undefined && String(value) === String(c.equals)
  }
  return true
}

/** `features.query` on the layers drawn now. */
export function featuresQuery(source: XrChatFeatureSource | null, command: Query): AgentToolOutcome {
  if (!source?.layers.length) {
    return unavailable('no-features', "The globe draws no records now (no points, lines or areas from the dataset's own data). Read values on the globe with the other tools.")
  }
  const layer = pickLayer(source.layers, command.layer)
  if (!layer) {
    return unavailable('no-layer', `No layer is called "${command.layer}". The layers drawn: ${source.layers.map((l) => l.name).join(', ')}.`)
  }
  const fields = attributes(layer)
  const numeric = fields.filter((f) => NUMERIC.has(f.type))
  const hasTime = layer.spec.time.mode !== 'static'

  // The ranking: a numeric field, or time; latest first by default.
  let sortField: VectorField | undefined
  let sortByTime = !command.sortBy && hasTime
  if (command.sortBy) {
    const name = command.sortBy.trim().toLowerCase()
    if (hasTime && ['time', 'date', 'start'].includes(name)) sortByTime = true
    else {
      sortField = fieldNamed(numeric, command.sortBy)
      if (!sortField) return unavailable('no-field', `No number field called "${command.sortBy}" to rank by. The number fields: ${fieldList(numeric) || 'none'}.`)
    }
  }
  const conditions: Array<{ field: VectorField; min?: number; max?: number; equals?: string | number | boolean }> = []
  for (const c of command.where ?? []) {
    const field = fieldNamed(fields, c.field)
    if (!field) return unavailable('no-field', `No field called "${c.field}". The fields: ${fieldList(fields)}.`)
    conditions.push({ ...c, field })
  }

  // The period: the frame on screen, or the whole layer narrowed by dates.
  const all = command.when === 'all' || command.from !== undefined || command.to !== undefined
  let pool: readonly VectorFeature[]
  let period: { from: string | null; to: string | null } | null = null
  if (!all && hasTime) {
    pool = source.shown(layer)
    if (source.clock && source.nowS !== null) {
      const end = frameEndAt(source.clock, source.nowS)
      const window = layer.spec.time.mode === 'instant' ? (layer.spec.time.snapshotWindowSeconds ?? source.clock.cadence) : 0
      period = window ? { from: isoS(end - window), to: isoS(end) } : { from: isoS(source.nowS), to: isoS(source.nowS) }
    }
  } else {
    const fromS = command.from ? dateS(command.from, false) : -Infinity
    const toS = command.to ? dateS(command.to, true) : Infinity
    pool = layer.features.filter((f) => {
      const t = span(layer, f)
      return !t || (t[1] >= fromS && t[0] < toS)
    })
    if (hasTime && source.clock) {
      const start = Math.max(source.clock.startS, fromS)
      const end = Math.min(timelineEnd(source.clock), toS)
      period = { from: isoS(start), to: isoS(end) }
    }
  }
  const total = pool.length
  const bounds = command.bounds
  const matched = pool.filter((f) =>
    (!bounds || vertices(f).some((p) => inBounds(p, bounds)))
    && conditions.every((c) => meets(f.attrs[c.field.name], c)))

  const desc = (command.order ?? 'desc') === 'desc'
  const key = (f: VectorFeature): number => {
    const v = sortByTime ? span(layer, f)?.[0] : sortField ? f.attrs[sortField.name] : undefined
    return typeof v === 'number' && Number.isFinite(v) ? v : NaN
  }
  const ranked = sortByTime || sortField
    ? [...matched].sort((a, b) => {
      const ka = key(a), kb = key(b)
      if (Number.isNaN(ka)) return Number.isNaN(kb) ? 0 : 1
      if (Number.isNaN(kb)) return -1
      return desc ? kb - ka : ka - kb
    })
    : matched
  const limit = Math.min(MAX_LIMIT, Math.max(1, command.limit ?? DEFAULT_LIMIT))
  // A title made only of fields listed anyway ("M{mag} · {place}") adds nothing but length.
  const template = localize(layer.spec.interaction?.title, LANG)
  const titled = !!template && [...template.matchAll(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g)].some((m) => !fields.some((f) => f.name === m[1]))

  const features = ranked.slice(0, limit).map((f) => {
    const out: Record<string, unknown> = {}
    const inspection = inspectFeature(layer, f, LANG)
    if (titled) out.title = inspection.title
    const t = span(layer, f)
    if (t) {
      if (t[0] === t[1]) out.time = isoS(t[0])
      else { out.start = isoS(t[0]); out.end = Number.isFinite(t[1]) ? isoS(t[1]) : 'ongoing' }
    }
    const at = placeOf(f)
    if (at) { out.lat = Math.round(at[1] * 1000) / 1000; out.lon = Math.round(at[0] * 1000) / 1000 }
    for (const field of fields) {
      const v = f.attrs[field.name]
      if (v === null || v === undefined) continue
      if (field.type === 'category') {
        const cat = field.categories?.find((c) => c.value === v)
        out[field.name] = cat ? localize(cat.label, LANG) : v
      } else out[field.name] = typeof v === 'number' ? round(v, field) : v
    }
    if (inspection.link) out.link = inspection.link
    return out
  })

  const summary: Record<string, { min: number; max: number; mean: number }> = {}
  for (const field of numeric) {
    const values = matched.map((f) => f.attrs[field.name]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    if (!values.length) continue
    let min = Infinity, max = -Infinity, sum = 0
    for (const v of values) { min = Math.min(min, v); max = Math.max(max, v); sum += v }
    summary[field.name] = { min: round(min, field), max: round(max, field), mean: round(sum / values.length, field, 1) }
  }

  const cautions: AgentCaution[] = []
  if (matched.length > features.length) cautions.push({ code: 'listed', text: `Only the first ${features.length} of the ${matched.length} that match are listed; the summary covers all ${matched.length}.` })
  if (!all && hasTime && source.playing) cautions.push({ code: 'playing', text: 'The globe is playing: these are the records of the date on screen when asked; it has moved on since.' })
  if (!matched.length) cautions.push({ code: 'none', text: 'No record matches: say so, and that there may be some at another date or with other conditions.' })

  return ok({
    layer: layer.name,
    geometry: layer.spec.geometry,
    when: all || !hasTime ? 'period' : 'shown',
    ...(period ? { period } : {}),
    fields: fields.map((f) => ({ name: f.name, label: labelOf(f), ...(f.units ? { units: f.units } : {}) })),
    total,
    matched: matched.length,
    sortedBy: sortField ? `${sortField.name} ${desc ? 'desc' : 'asc'}` : sortByTime ? `time ${desc ? 'desc (latest first)' : 'asc'}` : 'as published',
    features,
    summary,
    ...(source.layers.length > 1 ? { otherLayers: source.layers.filter((l) => l !== layer).map((l) => l.name) } : {}),
  }, FEATURES_METHOD, cautions)
}

/**
 * One line for the turn's context when the globe draws records: what they
 * are, their fields, and how many are on screen, so the model reads them
 * with `features_query` rather than from the colours.
 */
export function featuresSituation(source: XrChatFeatureSource | null): string | null {
  if (!source?.layers.length) return null
  const parts = source.layers.map((layer) => {
    const shown = layer.spec.time.mode === 'static' ? layer.features.length : source.shown(layer).length
    return `"${layer.name}" (${layer.spec.geometry}s; fields: ${fieldList(attributes(layer))}; ${shown} drawn for the date on screen, ${layer.features.length} in the whole period)`
  })
  return `The globe also draws records from the dataset itself: ${parts.join('; ')}. Read them with features_query, not from the colours.`
}
